import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiService from "../services/apiService";
import { RECON } from "../services/apiConstant";
import { uploadManager } from "../services/uploadManager";
import { ReconRequest } from "../types/vehicle";

/**
 * Hook to fetch all recon requests for a vehicle with nested quotes and items
 * Uses the new nested endpoint that returns everything in a single API call
 * @param vehicleInventoryId - Vehicle Inventory ID
 * @returns Query result with all recon requests including nested quotes
 */
export const useVehicleReconRequests = (vehicleInventoryId: string | undefined) => {
  return useQuery<ReconRequest[]>({
    queryKey: ["vehicles", vehicleInventoryId, "requests"],
    queryFn: async () => {
      if (!vehicleInventoryId) throw new Error("Vehicle inventory ID is required");

      // Use the new nested endpoint that returns requests with quotes and items
      const response = await apiService.get<{ success: boolean; requests: ReconRequest[] } | ReconRequest[]>(`${RECON.requestsByVehicle(vehicleInventoryId)}?status=!cancelled`);

      if ('success' in response && response.success && Array.isArray(response.requests)) {
        // Map quote_count from nested quotes
        return response.requests.map((request: ReconRequest) => ({
          ...request,
          quote_count: request.quotes?.length || 0,
        }));
      } else if (Array.isArray(response)) {
        return response;
      }

      return [];
    },
    enabled: !!vehicleInventoryId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

interface CreateReconRequestData {
  vehicle_inventory_id: string;
  user: string;
  description: string;
  phase: string;
  stage: string;
  requirement_type: string;
  eta: string;
  files?: File[];
}

interface CreateReconRequestResponse {
  success: boolean;
  request_id?: string;
  message?: string;
}

/**
 * Mutation hook to create a recon request with optional attachments
 */
export const useCreateReconRequest = () => {
  const queryClient = useQueryClient();

  return useMutation<CreateReconRequestResponse, Error, CreateReconRequestData>({
    mutationFn: async (data) => {
      const { files, ...requestData } = data;

      // Create the recon request
      const response = await apiService.post<CreateReconRequestResponse>(
        RECON.reconRequests,
        requestData
      );

      if (!response.success) {
        throw new Error(response.message || "Failed to create recon request");
      }

      // Queue attachments for async upload (non-blocking)
      if (files && files.length > 0 && response.request_id) {
        uploadManager.queueUploads(
          files,
          "recon_request",
          response.request_id,
          data.user,
          // Note: cache invalidation happens in onSuccess, uploads complete async
        );
      }

      return response;
    },
    onSuccess: (_data, variables) => {
      // Invalidate vehicle recon requests
      queryClient.invalidateQueries({ queryKey: ["vehicles", variables.vehicle_inventory_id, "requests"] });

      // Invalidate vehicle history - use predicate to match all variations of entityTypes filter
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return key[0] === "vehicle-history" && key[1] === variables.vehicle_inventory_id;
        }
      });

      // Invalidate entity attachments for recon requests
      queryClient.invalidateQueries({ queryKey: ["entity-attachments", "recon_request"] });

      // Invalidate recon requests count
      queryClient.invalidateQueries({ queryKey: ["reconRequestsCount", variables.vehicle_inventory_id] });
    },
  });
};

/**
 * Mutation hook to update recon request status
 */
export const useUpdateReconRequestStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, { requestId: string; status: string; vehicleInventoryId?: string }>({
    mutationFn: async ({ requestId, status }) => {
      const response = await apiService.patch<unknown>(
        `${RECON.reconRequests}/${requestId}`,
        { status }
      );
      return response;
    },
    onSuccess: (_data, variables) => {
      // Invalidate vehicle recon requests
      if (variables.vehicleInventoryId) {
        queryClient.invalidateQueries({ queryKey: ["vehicles", variables.vehicleInventoryId, "requests"] });
      }

      // Invalidate all vehicle queries (for list view)
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });

      // Invalidate vehicle history - use predicate to match all variations of entityTypes filter
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          if (variables.vehicleInventoryId) {
            return key[0] === "vehicle-history" && key[1] === variables.vehicleInventoryId;
          }
          return key[0] === "vehicle-history";
        }
      });

      // Invalidate recon requests count
      if (variables.vehicleInventoryId) {
        queryClient.invalidateQueries({ queryKey: ["reconRequestsCount", variables.vehicleInventoryId] });
      }
    },
  });
};

/**
 * Interface for updating recon request data
 */
export interface UpdateReconRequestData {
  requestId: string;
  vehicleInventoryId?: string;
  status?: string;
  description?: string;
  phase?: string;
  stage?: string;
  requirement_type?: "parts" | "service";
  eta?: string;
}

interface UpdateReconRequestResponse {
  success: boolean;
  message?: string;
  updated_fields?: string[];
}

/**
 * Mutation hook to update a recon request (all editable fields)
 */
export const useUpdateReconRequest = () => {
  const queryClient = useQueryClient();

  return useMutation<UpdateReconRequestResponse, Error, UpdateReconRequestData>({
    mutationFn: async ({ requestId, ...updateData }) => {
      // Filter out undefined values
      const payload: Record<string, unknown> = {};
      if (updateData.status !== undefined) payload.status = updateData.status;
      if (updateData.description !== undefined) payload.description = updateData.description;
      if (updateData.phase !== undefined) payload.phase = updateData.phase;
      if (updateData.stage !== undefined) payload.stage = updateData.stage;
      if (updateData.requirement_type !== undefined) payload.requirement_type = updateData.requirement_type;
      if (updateData.eta !== undefined) payload.eta = updateData.eta;

      const response = await apiService.patch<UpdateReconRequestResponse>(
        `${RECON.reconRequests}/${requestId}`,
        payload
      );

      return response;
    },
    onSuccess: (_data, variables) => {
      // Invalidate vehicle recon requests
      if (variables.vehicleInventoryId) {
        queryClient.invalidateQueries({ queryKey: ["vehicles", variables.vehicleInventoryId, "requests"] });
      }

      // Invalidate all vehicle queries (for list view)
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });

      // Invalidate vehicle history
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          if (variables.vehicleInventoryId) {
            return key[0] === "vehicle-history" && key[1] === variables.vehicleInventoryId;
          }
          return key[0] === "vehicle-history";
        }
      });

      // Invalidate recon requests count
      if (variables.vehicleInventoryId) {
        queryClient.invalidateQueries({ queryKey: ["reconRequestsCount", variables.vehicleInventoryId] });
      }
    },
  });
};

